import { distinctUntilChanged, first, withLatestFrom } from "rxjs/operators";
import Point from "ol/geom/Point";
import LineString from "ol/geom/LineString";
import Style from "ol/style/Style";
import { GeoJSON } from "ol/format";
import VectorTileLayer from "ol/layer/VectorTile";
import VectorTileSource from "ol/source/VectorTile";
import { Fill, Stroke, Circle } from "ol/style";
import Feature from "ol/Feature";
/**
 * @class FramesLayer
 * @classdesc Shows the current position of the Infra3D-Image in the map.
 */
export class FramesLayer {
    constructor(navigator, map, style, routesconfig) {
        this._layer = null;
        this._map = null;
        this._navigator = null;
        this._hover_layer = null;
        this._selected_feature = null;
        this._map = map;
        this._navigator = navigator;
        this.init(routesconfig.min_zoom, routesconfig.max_zoom, style);
        this.init_hover_layer(routesconfig.min_zoom, routesconfig.max_zoom);
    }
    get layer() {
        return this._layer;
    }
    init(min_zoom, max_zoom, style) {
        const fill_background = new Fill({
            color: "rgba(5, 57, 82, 0.8)",
        });
        const stroke_background = new Stroke({
            color: "rgba(5, 57, 82, 0.8)",
            width: 2,
        });
        const defaultstyle_background = new Style({
            image: new Circle({
                fill: fill_background,
                radius: 3,
            }),
            stroke: stroke_background,
            zIndex: 200,
        });
        let current_campaign = null;
        const tile_overlap = 6;
        this._layer = new VectorTileLayer({
            map: this._map,
            renderMode: "vector",
            zIndex: 210,
            style: (feature, resolution) => {
                if (feature.getProperties()["campaign_uid"] == current_campaign) {
                    return style(feature, resolution);
                }
                else {
                    return defaultstyle_background;
                }
            },
            minZoom: min_zoom,
            maxZoom: max_zoom,
            source: new VectorTileSource({
                format: new GeoJSON(),
                tileSize: 512,
                overlaps: false,
                cacheSize: 2,
                minZoom: min_zoom,
                maxZoom: max_zoom,
                tileLoadFunction: (tile, url) => {
                    tile.setLoader((extent, resolution, projection) => {
                        const srid = parseInt(projection.getCode().split(":").slice(-1)[0]);
                        this._navigator.api.data
                            .getFramesAsGeoJSON(extent, srid, undefined, 5000, tile_overlap)
                            .then((geojson) => {
                            let features = [];
                            if (geojson.features.length > 0) {
                                features = FramesLayer.frames_to_eventfeatures(geojson);
                            }
                            tile.setFeatures(features);
                        })
                            .catch((error) => {
                            tile.setFeatures([]);
                            console.warn(error);
                        });
                    });
                },
                url: "{z}/{x}/{y}",
            }),
        });
        this._navigator.stateService.currentNode$
            .pipe(withLatestFrom(this._navigator.playService.playing$), distinctUntilChanged(([x, playing_x], [y, playing_y]) => {
            if (playing_x)
                return false;
            if (!x.full)
                return false;
            return x.frame.campaignID == y.frame.campaignID;
        }))
            .subscribe(([node, playing]) => {
            if (!playing) {
                current_campaign = node.frame.campaignID;
                this._layer.changed();
            }
        });
    }
    init_hover_layer(min_zoom, max_zoom) {
        if (!this._layer)
            return;
        //todo: make warning; can not init selection if no layer is initialized
        this._map.on("click", (event) => {
            if (!this._layer)
                return;
            if (!this._layer.getRenderer())
                return;
            if (event.target.getView().getZoom() <= min_zoom || event.target.getView().getZoom() > max_zoom)
                return;
            if (!this._selected_feature) {
                const features = this._map.getFeaturesAtPixel(event.pixel, {
                    hitTolerance: 10, // in css pixel
                    layerFilter: (layer) => {
                        return layer === this._layer;
                    },
                });
                if (!features.length) {
                    this._selected_feature = null;
                    // Tooltip currently deactivated, as not active in routes
                    // if (tooltip_callback) tooltip_callback(event.coordinate, null);
                    if (this._hover_layer)
                        this._hover_layer.changed();
                    return;
                }
                const features_filtered = features.filter((feature) => feature.getGeometry().getType() === "Point");
                const feature = features_filtered[0];
                if (!feature) {
                    return;
                }
                this._selected_feature = feature;
                if (this._hover_layer)
                    this._hover_layer.changed();
            }
            this._move_to_selected_feature();
        });
        this._map.on("pointermove", (event) => {
            if (!this._layer)
                return;
            if (!this._layer.getRenderer())
                return;
            if (event.target.getView().getZoom() <= min_zoom || event.target.getView().getZoom() > max_zoom)
                return;
            if (!this._layer.getVisible())
                return;
            this._layer.getFeatures(event.pixel).then((features) => {
                if (!features.length) {
                    this._selected_feature = null;
                    // Tooltip currently deactivated, as not active in routes
                    // if (tooltip_callback) tooltip_callback(event.coordinate, null);
                    if (this._hover_layer)
                        this._hover_layer.changed();
                    return;
                }
                const features_filtered = features.filter((feature) => feature.getGeometry().getType() === "Point");
                const feature = features_filtered[0];
                if (!feature) {
                    return;
                }
                this._selected_feature = feature;
                if (this._hover_layer)
                    this._hover_layer.changed();
                // Tooltip currently deactivated, as not active in routes
                // if (tooltip_callback) tooltip_callback(event.coordinate, feature);
            });
        });
        const fillHighlight = new Fill({
            color: "rgba(255, 103, 0, 1)",
        });
        const selected_feature_style = new Style({
            image: new Circle({
                fill: fillHighlight,
                radius: 5,
            }),
        });
        // Selection
        this._hover_layer = new VectorTileLayer({
            map: this._map,
            renderMode: "vector",
            maxResolution: 10,
            source: this._layer.getSource(),
            minZoom: min_zoom,
            maxZoom: max_zoom,
            style: (feature) => {
                if (this._selected_feature) {
                    if (feature.getId() === this._selected_feature.getId()) {
                        return selected_feature_style;
                    }
                }
            },
        });
    }
    _move_to_selected_feature() {
        if (this._selected_feature) {
            const frame_candidates = this._selected_feature.getProperties()["frames"];
            const key_candidates = frame_candidates.map((x) => x.uid);
            let key = frame_candidates[0].uid;
            this._navigator.api.data.getFullImages(key_candidates).then((nodes) => {
                const full_frame_candidates = Object.keys(nodes).map((x) => nodes[x].frame);
                this._navigator.stateService.currentNode$.pipe(first()).subscribe((node) => {
                    const sortscore = (f, node) => {
                        const f_same_streamtype = f.streamtype == node.frame.streamtype ? 0 : 1;
                        const f_same_orientation = f.capturesystem.orientation == node.frame.capturesystem.orientation ? 0 : 1;
                        const f_same_sensorgroup = f.capturesystem.sensorgroup == node.frame.capturesystem.sensorgroup ? 0 : 1;
                        return f_same_streamtype + f_same_orientation + f_same_sensorgroup;
                    };
                    const frames_sorted = full_frame_candidates.sort((a, b) => {
                        return sortscore(a, node) - sortscore(b, node);
                    });
                    if (frames_sorted.length > 0) {
                        key = frames_sorted[0].key;
                    }
                    this._navigator.moveToKey$(key).subscribe(() => {
                        this._selected_feature = null;
                        if (this._hover_layer)
                            this._hover_layer.changed();
                    });
                });
            });
        }
    }
    /**
     * Groups Geojson-Features Layer into grouped points and lines
     * @param geojson
     * @param skip
     * @returns {Feature[]}
     */
    static frames_to_eventfeatures(geojson) {
        // group features by its timestamp and campaign.
        const group = geojson.features.reduce((r, a) => {
            const { timestamp, campaignID } = a["properties"];
            const key = `${timestamp}-${campaignID}`;
            r[key] = [...(r[key] || []), a];
            return r;
        }, {});
        // make a point feature out of timestamp-grouped features. calculate the geometric center.
        const groupded_features = Object.keys(group).map((x) => {
            const agroup = group[x];
            const eastings = agroup.map((y) => y["geometry"]["coordinates"][0]);
            const average_easting = eastings.reduce(function (avg, value, _, { length }) {
                return avg + value / length;
            }, 0);
            const northings = agroup.map((y) => y["geometry"]["coordinates"][1]);
            const average_northing = northings.reduce(function (avg, value, _, { length }) {
                return avg + value / length;
            }, 0);
            const frame_properties = {
                frames: agroup.map((y) => y["properties"]),
            };
            const feature_properties = {
                ...frame_properties,
                ...agroup[0]["properties"],
            };
            const feature = new Feature({
                geometry: new Point([average_easting, average_northing]),
            });
            feature.setProperties(feature_properties);
            feature.setId(agroup[0]["properties"]["uid"]);
            return feature;
        });
        // make line feautures out of two follwing points. connects them only to a line if distance and timedifference are below a threshold
        const final_lines = [];
        const campaigns = groupded_features.reduce((r, a) => {
            const key = a.getProperties().campaign_uid;
            r[key] = [...(r[key] || []), a];
            return r;
        }, {});
        for (const campaign_key of Object.keys(campaigns)) {
            const groupded_features = campaigns[campaign_key];
            // calculate the timestamp in milliseconds for each feature and sort them.
            const timestamps_seconds = groupded_features.map((x) => {
                const timestamp_seconds = new Date(x.getProperties().timestamp).getTime();
                return [timestamp_seconds, x];
            });
            timestamps_seconds.sort(([a, fa], [b, fb]) => {
                return a - b;
            });
            timestamps_seconds.reduce(([a, fa], [r, fr]) => {
                const dx = fr.getGeometry().getCoordinates()[0] - fa.getGeometry().getCoordinates()[0];
                const dy = fr.getGeometry().getCoordinates()[1] - fa.getGeometry().getCoordinates()[1];
                const dist = Math.sqrt(Math.pow(dx, 2) + Math.pow(dy, 2));
                const timediff = r - a;
                if (dist < 20 && timediff < 60000) {
                    const feature = new Feature({
                        geometry: new LineString([
                            fa.getGeometry().getCoordinates(),
                            fr.getGeometry().getCoordinates(),
                        ]),
                    });
                    for (const key of fa.getKeys()) {
                        if (key != "geometry")
                            feature.set(key, fa.get(key));
                    }
                    final_lines.push(feature);
                }
                return [r, fr];
            }, timestamps_seconds[0]);
        }
        return groupded_features.concat(final_lines);
    }
    setVisible(visible) {
        this._layer.setVisible(visible);
        this._hover_layer.setVisible(visible);
        this._layer.changed();
        this._hover_layer.changed();
    }
}
//# sourceMappingURL=FramesLayer.js.map